import React, { useState, useEffect, useCallback, useRef } from 'react'
import { controlCenterApi, type SystemHealthData } from '../../services/controlCenterApi'

const REFRESH_OPTIONS = [
  { value: 0, label: 'Off' },
  { value: 30, label: '30s' },
  { value: 60, label: '1m' },
  { value: 300, label: '5m' },
]

function formatLatency(seconds: number): string {
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`
}

function latencyColor(seconds: number): string {
  if (seconds < 5) return 'text-emerald-400'
  if (seconds < 30) return 'text-amber-400'
  return 'text-red-400'
}

function latencyBg(seconds: number): string {
  if (seconds < 5) return 'bg-emerald-500/10 border-emerald-500/20'
  if (seconds < 30) return 'bg-amber-500/10 border-amber-500/20'
  return 'bg-red-500/10 border-red-500/20'
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toLocaleString()
}

function timeAgo(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000
  if (diff < 60) return `${Math.round(diff)}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}

export function SystemHealthDashboard(): React.ReactElement {
  const [data, setData] = useState<SystemHealthData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [refreshInterval, setRefreshInterval] = useState(0)
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchHealth = useCallback(async () => {
    try {
      const health = await controlCenterApi.getSystemHealth()
      setData(health)
      setLastRefresh(new Date())
      setError('')
    } catch {
      setError('Failed to fetch system health data')
    }
    setIsLoading(false)
  }, [])

  useEffect(() => {
    fetchHealth()
  }, [fetchHealth])

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    if (refreshInterval > 0) {
      intervalRef.current = setInterval(fetchHealth, refreshInterval * 1000)
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [refreshInterval, fetchHealth])

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-slate-400 text-sm mt-3">Loading system health...</span>
      </div>
    )
  }

  if (error && !data) {
    return (
      <div className="p-6 bg-red-500/10 border border-red-500/30 rounded-lg">
        <p className="text-red-300 text-sm">{error}</p>
        <button type="button" onClick={fetchHealth} className="text-red-400 hover:text-red-300 text-sm mt-2 underline">
          Retry
        </button>
      </div>
    )
  }

  if (!data) return <div />

  const overview = data.overview

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-white tracking-tight">System Health</h1>
          <p className="text-slate-500 text-xs mt-1 font-mono">
            {lastRefresh ? `Last updated ${lastRefresh.toLocaleTimeString()}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={fetchHealth}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
            title="Refresh now"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          <div className="flex items-center bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-2.5">Auto</span>
            {REFRESH_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setRefreshInterval(opt.value)}
                className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${
                  refreshInterval === opt.value
                    ? 'bg-primary text-white'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}

      {/* Overview cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {[
          { label: 'Processed', value: overview.processed, color: 'text-emerald-400' },
          { label: 'Failed', value: overview.failed, color: overview.failed > 0 ? 'text-red-400' : 'text-slate-400' },
          { label: 'Enqueued', value: overview.enqueued, color: overview.enqueued > 0 ? 'text-primary' : 'text-slate-400' },
          { label: 'Scheduled', value: overview.scheduled, color: 'text-slate-400' },
          { label: 'Retries', value: overview.retries, color: overview.retries > 0 ? 'text-amber-400' : 'text-slate-400' },
          { label: 'Dead', value: overview.dead, color: overview.dead > 0 ? 'text-red-400' : 'text-slate-400' },
        ].map(card => (
          <div key={card.label} className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{card.label}</p>
            <p className={`text-2xl font-bold font-mono ${card.color}`}>
              {formatNumber(card.value)}
            </p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Queues */}
        <section>
          <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Queues</h2>
          <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
            {data.queues.length === 0 ? (
              <p className="text-slate-500 text-sm p-5">No queues active</p>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-4 py-2.5">Queue</th>
                    <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-4 py-2.5">Size</th>
                    <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-4 py-2.5">Latency</th>
                  </tr>
                </thead>
                <tbody>
                  {data.queues.map(q => (
                    <tr key={q.name} className="border-b border-slate-700/50 last:border-0">
                      <td className="px-4 py-3">
                        <span className="text-sm font-mono text-white">{q.name}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-sm font-mono ${q.size > 0 ? 'text-primary' : 'text-slate-500'}`}>
                          {q.size.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`inline-block text-xs font-mono px-2 py-0.5 rounded border ${latencyBg(q.latency)} ${latencyColor(q.latency)}`}>
                          {formatLatency(q.latency)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* Redis & Workers */}
        <div className="space-y-6">
          {/* Redis */}
          <section>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Redis</h2>
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-2 h-2 rounded-full ${data.redis.connected ? 'bg-emerald-400' : 'bg-red-400'}`} />
                <span className="text-sm text-white font-medium">
                  {data.redis.connected ? 'Connected' : 'Disconnected'}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Memory</p>
                  <p className="text-sm font-mono text-white mt-0.5">{data.redis.used_memory_human}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Clients</p>
                  <p className="text-sm font-mono text-white mt-0.5">{data.redis.connected_clients}</p>
                </div>
                {data.redis.uptime_in_days !== undefined && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Uptime</p>
                    <p className="text-sm font-mono text-white mt-0.5">{data.redis.uptime_in_days}d</p>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Workers */}
          <section>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
              Workers
              <span className="ml-2 text-slate-600">
                {data.busy_count}/{data.workers.reduce((sum, w) => sum + w.concurrency, 0)} busy
              </span>
            </h2>
            <div className="bg-slate-800 rounded-xl border border-slate-700">
              {data.workers.length === 0 ? (
                <p className="text-slate-500 text-sm p-5">No workers running</p>
              ) : (
                <div className="divide-y divide-slate-700/50">
                  {data.workers.map((w, i) => (
                    <div key={`${w.hostname}-${w.pid}-${i}`} className="px-4 py-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-mono text-white">{w.hostname}</span>
                        <span className="text-xs font-mono text-slate-500">PID {w.pid}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className={`text-xs font-mono ${w.busy > 0 ? 'text-primary' : 'text-slate-500'}`}>
                          {w.busy}/{w.concurrency} threads
                        </span>
                        <span className="text-xs text-slate-600">
                          {w.queues.join(', ')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* Failed Jobs */}
      <section>
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
          Recent Failures
          {data.failed_jobs.length > 0 && (
            <span className="ml-2 text-red-400">{data.failed_jobs.length}</span>
          )}
        </h2>
        <div className="bg-slate-800 rounded-xl border border-slate-700">
          {data.failed_jobs.length === 0 ? (
            <div className="p-5 text-center">
              <p className="text-slate-500 text-sm">No recent failures</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-700/50">
              {data.failed_jobs.map(job => (
                <div key={job.jid} className="px-5 py-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono text-white">{job.class}</span>
                        <span className="text-[10px] font-mono text-slate-600 bg-slate-900 px-1.5 py-0.5 rounded">{job.queue}</span>
                        {job.retry_count > 0 && (
                          <span className="text-[10px] font-mono text-amber-400">retry #{job.retry_count}</span>
                        )}
                      </div>
                      {job.error_message && (
                        <p className="text-xs text-red-400/80 mt-1 font-mono truncate">{job.error_message}</p>
                      )}
                    </div>
                    {job.failed_at && (
                      <span className="text-[10px] text-slate-600 font-mono shrink-0">{timeAgo(job.failed_at)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
